import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ReadMoreComponent } from './components/read-more/read-more.component';
import { BookingWidgetComponent } from './components/booking-widget/booking-widget.component';
import { DateMaskValidatorDirective } from './directives/date-mask/date-mask-validator.directive';
import { GalleryDirective } from './directives/gallery/gallery.directive';
import { NoResultsComponent } from './components/no-results/no-results.component';
import { GalleryComponent } from './components/gallery/gallery.component';
import { CommonAppModule } from './modules/common-app/common-app.module';
import { InputNumbersDirective } from './directives/input-numbers/input-numbers.directive';
import { PhoneFieldComponent } from './components/phone-field/phone-field.component';
import { WidgetParamsComponent } from './components/booking-widget/components/widget-params/widget-params.component';
import { BookingRefundComponent } from './components/booking-widget/components/booking-refund/booking-refund.component';
import { BookingHeadingComponent } from './components/booking-widget/components/booking-heading/booking-heading.component';
import { BookingCheckoutDetailsComponent } from './components/booking-widget/components/booking-checkout-details/booking-checkout-details.component';
import { BookingTotalInfoComponent } from './components/booking-widget/components/booking-total-info/booking-total-info.component';
import { FindObjIndexPipe } from './pipes/find-obj-index/find-obj-index.pipe';
import { TagImageComponent } from './components/tag-image/tag-image.component';
import { DateMaskDirective } from './directives/date-mask/date-mask.directive';
import { SuiteInfoComponent } from './components/suite-info/suite-info.component';
import { SuitePictureComponent } from './components/suite-picture/suite-picture.component';
import { ImageFallbackDirectiveModule } from './modules/image-fallback-directive/image-fallback-directive.module';
import { PdpAvailabilityOrderPipe } from './pipes/pdp-availability-order/pdp-availability-order.pipe';
import { SuiteInfoSpacesComponent } from './components/suite-info-spaces/suite-info-spaces.component';
import { BestPriceComponent } from './components/best-price/best-price.component';
import { PhoneNumberComponent } from './components/phone-number/phone-number.component';
import { FreeCancellationDatePipe } from './pipes/free-cancellation-date/free-cancellation-date.pipe';
import { ReviewWidgetComponent } from './components/review-widget/review-widget.component';
import { FormatSuiteNamePipe } from '../suites/pipes/format-suite-name/format-suite-name.pipe';
import { SelectComponent } from './components/select/select.component';
import { DetailGalleryComponent } from './components/detail-gallery/detail-gallery.component';
import { PageTopNavigationComponent } from './components/page-top-navigation/page-top-navigation.component';
import { ClearInputDirective } from './directives/clear-input/clear-input.directive';
import { BestPriceTooltipComponent } from './components/best-price-tooltip/best-price-tooltip.component';
import { BestPriceTooltipDirective } from './directives/best-price-tooltip/best-price-tooltip.directive';
import { DirectivesModule } from './directives/directive.module';
import { BookingDetailsLinkPipe } from '../account/pipes/booking-details-link/booking-details-link.pipe';
import { EmptyDataBannerComponent } from './components/empty-data-banner/empty-data-banner.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { PillButtonComponent } from './components/pill-button/pill-button.component';
import { InputTextComponent } from './components/input-text/input-text.component';
import { TranslateSplitPipe } from './pipes/translate-split/translate-split';
import { SrTextComponent } from './components/sr-text/sr-text.component';
import { GuestCapacityComponent } from './components/guest-capacity/guest-capacity.component';
import { DrawerComponent } from './components/drawer/drawer.component';
import { DrawerHeaderComponent } from './components/drawer/drawer-header/drawer-header.component';
import { TrapFocusDirective } from './directives/trap-focus/trap-focus.directive';
import { MenuListComponent } from './components/menu-list/menu-list.component';
import { MenuListItemComponent } from './components/menu-list-item/menu-list-item.component';
import {
  ButtonDirective,
  InputModule,
  FormFieldDirective,
  FormErrorDirective,
  FormDescriptionDirective,
  CheckboxDirective,
  LabelDirective
} from '@limehome/spark-ui-angular';
import { NgIconsModule } from '@ng-icons/core';

import { PluralizePipeModule } from './modules/pluralize-pipe/pluralize-pipe.module';
import {
  faSolidCircleCheck,
  faSolidCircleInfo,
  faSolidCircleQuestion,
  faSolidTriangleExclamation
} from '@ng-icons/font-awesome/solid';
import { AddonButtonComponent } from './components/addon-button/addon-button.component';

@NgModule({
  declarations: [
    AddonButtonComponent,
    BestPriceComponent,
    BestPriceTooltipComponent,
    BestPriceTooltipDirective,
    BookingCheckoutDetailsComponent,
    BookingDetailsLinkPipe,
    BookingHeadingComponent,
    BookingRefundComponent,
    BookingTotalInfoComponent,
    BookingWidgetComponent,
    ClearInputDirective,
    DateMaskDirective,
    DateMaskValidatorDirective,
    DetailGalleryComponent,
    DrawerComponent,
    DrawerHeaderComponent,
    EmptyDataBannerComponent,
    FindObjIndexPipe,
    FormatSuiteNamePipe,
    FreeCancellationDatePipe,
    GalleryComponent,
    GalleryDirective,
    GuestCapacityComponent,
    InputNumbersDirective,
    InputTextComponent,
    MenuListComponent,
    MenuListItemComponent,
    NoResultsComponent,
    PageTopNavigationComponent,
    PdpAvailabilityOrderPipe,
    PhoneFieldComponent,
    PhoneNumberComponent,
    PillButtonComponent,
    ReadMoreComponent,
    ReviewWidgetComponent,
    SelectComponent,
    SrTextComponent,
    SuiteInfoComponent,
    SuiteInfoSpacesComponent,
    SuitePictureComponent,
    TagImageComponent,
    TooltipComponent,
    TranslateSplitPipe,
    TrapFocusDirective,
    WidgetParamsComponent
  ],
  imports: [
    ButtonDirective,
    CheckboxDirective,
    CommonAppModule,
    CommonModule,
    DirectivesModule,
    FormDescriptionDirective,
    FormErrorDirective,
    FormFieldDirective,
    ImageFallbackDirectiveModule,
    InputModule,
    LabelDirective,
    NgOptimizedImage,
    PluralizePipeModule,
    NgIconsModule.withIcons({
      faSolidTriangleExclamation,
      faSolidCircleCheck,
      faSolidCircleInfo,
      faSolidCircleQuestion
    })
  ],
  exports: [
    AddonButtonComponent,
    BestPriceComponent,
    BestPriceTooltipComponent,
    BestPriceTooltipDirective,
    BookingDetailsLinkPipe,
    BookingWidgetComponent,
    ButtonDirective,
    CheckboxDirective,
    ClearInputDirective,
    CommonAppModule,
    DateMaskDirective,
    DateMaskValidatorDirective,
    DetailGalleryComponent,
    DrawerComponent,
    EmptyDataBannerComponent,
    FormDescriptionDirective,
    FormErrorDirective,
    FormFieldDirective,
    FreeCancellationDatePipe,
    GalleryDirective,
    GuestCapacityComponent,
    ImageFallbackDirectiveModule,
    InputModule,
    InputNumbersDirective,
    InputTextComponent,
    LabelDirective,
    MenuListComponent,
    MenuListItemComponent,
    NoResultsComponent,
    PageTopNavigationComponent,
    PdpAvailabilityOrderPipe,
    PhoneFieldComponent,
    PhoneNumberComponent,
    PillButtonComponent,
    ReadMoreComponent,
    ReviewWidgetComponent,
    SelectComponent,
    SrTextComponent,
    SuiteInfoComponent,
    SuiteInfoSpacesComponent,
    SuitePictureComponent,
    TagImageComponent,
    TooltipComponent,
    TranslateSplitPipe
  ],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedModule {}
